import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Skeleton, Space, type TabsProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import Button from 'components/new-ui/base/button/Button';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import { StyledTabs } from 'components/new-ui/base/tabs/styles';
import { StyledPageWrapper } from 'components/new-ui/base/wrappers/styles';
import useBreadcrumb from 'hooks/useBreadcrumb';
import { getManifestJobActiveTabKey } from './utils';
import { ManifestJobTabKey, ManifestJobTabKeys } from './types';
import NewDeclarationDrawer, { NewDeclarationDrawerFormValues } from 'views/declarations/common/NewDeclarationDrawer';
import { JobStatuses, UpdateExternalReferencesPayload } from 'store/jobs/job';
import JobStatus from 'views/jobs/JobStatus';
import { getJobEmailNotificationStatus, updateExternalReferences } from 'store/jobs/client';
import Notification from 'components/ui/base/notification/Notification';
import moment from 'moment';
import useManifests from 'hooks/useManifests';
import { BrokerUploadLocationState } from 'views/invoice-upload/broker-upload/types';
import { StyledBadge } from 'components/new-ui/base/status-indicator/styles';
import { hasPermissions } from 'config/roles/hasPermissions';
import useSession from 'hooks/useSession';
import ExternalReferencesModal from 'views/manifests/components/ExternalReferencesModal';
import { getErrorMessage } from 'utils/getErrorMessage';
import { ExternalReference, getExternalReferenceFromManifestJob } from '../../../../utils/external-references';
import useJobs from 'hooks/useJobs';
import DotSeparator from 'components/new-ui/base/dot-separator/DotSeparator';
import { useRequestPromise } from 'hooks/useRequest';
import EmailJobModal from 'views/manifests/manifest-details/components/email-job/EmailJobModal';
import { EmailNotificationOverallStatuses, EmailNotificationProviderStatuses } from 'types/common';
import EmailNotificationStatusTag from 'components/ui/composed/email-notification/EmailNotificationStatusTag';
import EditJobModal from 'views/jobs/components/edit-job/EditJobModal';

type TabItem = Exclude<TabsProps['items'], undefined>[number];
type Modal = 'create-declaration' | 'add-external-reference' | 'email-job' | 'edit' | null;

interface Props {
    children: ReactNode;
}

const ManifestJobDetails = ({ children }: Props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userInfo } = useSession();
    const { manifestId, jobId } = useParams<{ manifestId: string; jobId: string }>();
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const { manifest, isLoading: isLoadingManifest, getManifestById } = useManifests();
    const { getJob, job, invoicesCount, getJobInvoicesCount } = useJobs({ jobId });
    const { data: emailNotificationsData, isLoading: isLoadingEmailNotificationsData } = useRequestPromise(
        () => {
            if (!manifestId || !jobId) return null;
            return getJobEmailNotificationStatus(manifestId, jobId);
        },
        {},
        [manifestId, jobId]
    );

    const [modal, setModal] = useState<Modal>(null);
    const [hasEmailBeenSent, setHasEmailBeenSent] = useState(false);

    const activeKey = getManifestJobActiveTabKey(location);
    const canCreateDeclarations = hasPermissions(userInfo, 'declarations', 'CREATE');
    const canUpdateJobExternalReferences = hasPermissions(userInfo, 'jobs', 'UPDATE_EXTERNAL_REFERENCES');
    const canUpdateJobs = hasPermissions(userInfo, 'jobs', 'UPDATE');
    const isDraftJob = job?.status === JobStatuses.NEW;
    const isClearedJob = job?.status === JobStatuses.COMPLETED;

    const createdBy = job?.creator?.fullName ?? job?.creator?.email ?? '-';
    const createdAt = job?.createdAt;
    const formattedCreatedAt = moment(createdAt).format('DD/MM/YYYY');
    const createdByText = `${createdBy}, ${formattedCreatedAt}`;

    const emailNotificationStatus = useMemo((): {
        overallStatus: EmailNotificationOverallStatuses;
        providerStatus?: EmailNotificationProviderStatuses;
    } | null => {
        const overallStatus = emailNotificationsData?.overallStatus;
        const providerStatus = emailNotificationsData?.overallProviderEventType;

        if (hasEmailBeenSent) {
            return {
                overallStatus:
                    overallStatus === EmailNotificationOverallStatuses.SENT
                        ? EmailNotificationOverallStatuses.SENT
                        : EmailNotificationOverallStatuses.SENDING,
                providerStatus: undefined,
            };
        }

        if (!overallStatus || overallStatus === 'NOT_SENT') return null;

        return {
            overallStatus,
            providerStatus,
        };
    }, [emailNotificationsData, hasEmailBeenSent]);

    const closeModal = () => setModal(null);

    // Job under manifest has only UK_CDS_MRN as external reference
    const handleUpdateExternalReference = async (externalReference: ExternalReference) => {
        const payload: UpdateExternalReferencesPayload = {
            cdsExportMRN: externalReference.referenceNumber,
        };

        try {
            if (!job?.id) {
                throw new Error('Could not get data for this job. Please refresh the page and try again.');
            }

            await updateExternalReferences(job.id, payload);
            await getJob(job.id);

            Notification({
                placement: 'top',
                type: 'success',
                messageTitle: 'External Reference Added',
                description: 'External reference has been successfully added to the job.',
            });
        } catch (error) {
            const err = getErrorMessage(error);
            Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'External Reference Error',
                description: err || 'Could not add external reference to this job.',
            });
        }
    };

    const handleRemoveExternalReference = async (ref: ExternalReference) => {
        if (!manifestId || !manifest || !job) {
            return Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'External Reference Error',
                description: 'Could not get manifest and job data. Please try again after reloading the page.',
            });
        }

        try {
            const payload: UpdateExternalReferencesPayload = {
                cdsExportMRN: null,
            };

            await updateExternalReferences(job.id, payload);
            await getJob(job.id);

            Notification({
                placement: 'top',
                type: 'success',
                messageTitle: 'External Reference Added',
                description: 'External reference has been successfully removed.',
            });
        } catch (err) {
            const error = getErrorMessage(err);
            Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'External Reference Error',
                description: error || 'Could not add external reference to this job.',
            });
        }
    };

    useEffect(() => {
        setBreadcrumbRoutes([
            { breadcrumbName: 'Home', path: '/home' },
            { breadcrumbName: 'Manifests', path: '../manifests' },
            { breadcrumbName: manifest?.referenceId ?? '-', path: `../manifests/${manifestId}` },
            { breadcrumbName: job?.referenceId ?? '-', path: '' },
        ]);
    }, [setBreadcrumbRoutes, manifestId, job?.referenceId, manifest?.referenceId]);

    useEffect(() => {
        if (!manifestId) return;

        // If nothing loaded yet, or a different manifest is in store, fetch the one from the URL
        if (!manifest || String(manifest.id) !== String(manifestId)) {
            getManifestById(manifestId);
        }
    }, [manifestId, manifest?.id, getManifestById, manifest]);

    const handleCreateNewDeclaration = () => {
        setModal('create-declaration');
    };

    const handleNavigateBack = () => {
        navigate(-1);
    };

    const handleNavigateWithDataState = ({ country, declarationType }: NewDeclarationDrawerFormValues) => {
        const state: BrokerUploadLocationState = {
            job,
            jobLocked: true,
            manifestId: manifestId,
        };
        navigate(`create-declaration/${country}/${declarationType}`, { state });
    };

    // Get invoices count for the job
    useEffect(() => {
        if (jobId) {
            getJobInvoicesCount(jobId);
        }
    }, [jobId, getJobInvoicesCount]);

    const tabsItems: TabItem[] = useMemo(
        () => [
            { key: ManifestJobTabKeys.OVERVIEW, label: 'Overview' },
            { key: ManifestJobTabKeys.DOCUMENTS, label: 'Documents' },
            { key: ManifestJobTabKeys.NOTES, label: 'Notes' },
            {
                key: ManifestJobTabKeys.INVOICES,
                label: <>Invoices {invoicesCount > 0 && <StyledBadge color="var(--gray-6)" count={invoicesCount} />}</>,
            },
            { key: ManifestJobTabKeys.HISTORY, label: 'History' },
        ],
        [invoicesCount]
    );

    if (isLoadingManifest) return null;

    return (
        <StyledPageWrapper>
            <PageHeader
                title="Job Details"
                icon={null}
                showBackButton
                onBackButtonClick={handleNavigateBack}
                additionalContent={
                    <div style={{ display: 'flex', gap: 16, marginLeft: 16, alignItems: 'center' }}>
                        <div>
                            <span>Job ID: </span>
                            <span>{job?.referenceId ?? '-'}</span>
                        </div>
                        <JobStatus status={job?.status as JobStatuses} />
                        <DotSeparator />

                        {isLoadingEmailNotificationsData ? (
                            <Skeleton.Input size="small" />
                        ) : emailNotificationStatus ? (
                            <>
                                <EmailNotificationStatusTag
                                    overallStatus={emailNotificationStatus.overallStatus}
                                    providerStatus={emailNotificationStatus.providerStatus}
                                />
                                <DotSeparator />
                            </>
                        ) : null}

                        <div>
                            <span>Created By: </span>
                            <span>{createdByText}</span>
                        </div>
                    </div>
                }
                sidebar={
                    <Space size={8}>
                        {isClearedJob && <Button onClick={() => setModal('email-job')}>Email Job</Button>}

                        {isDraftJob && canUpdateJobs && <Button onClick={() => setModal('edit')}>Edit Job</Button>}

                        {canCreateDeclarations && (
                            <Button icon={<PlusOutlined />} type="primary" onClick={handleCreateNewDeclaration}>
                                New Declaration
                            </Button>
                        )}
                    </Space>
                }
            />
            <StyledTabs
                style={{
                    marginTop: 24,
                }}
                activeKey={activeKey}
                onChange={(k) => {
                    const activeKey = k as ManifestJobTabKey;
                    const to =
                        activeKey === ManifestJobTabKeys.OVERVIEW ? `/manifests/${manifestId}/${jobId}` : activeKey;
                    navigate(to, {
                        replace: true,
                    });
                }}
                items={tabsItems}
                tabBarExtraContent={
                    <>
                        {canUpdateJobExternalReferences && (
                            <Button onClick={() => setModal('add-external-reference')}>Add External Reference</Button>
                        )}
                    </>
                }
            />
            {children}

            <NewDeclarationDrawer
                open={modal === 'create-declaration'}
                onClose={closeModal}
                onSelectDeclarationInfo={handleNavigateWithDataState}
            />

            {job && <EditJobModal job={job} open={modal === 'edit'} onClose={closeModal} />}

            {job && (
                <ExternalReferencesModal
                    type="manifestJob"
                    externalReferences={getExternalReferenceFromManifestJob(job)}
                    open={modal === 'add-external-reference'}
                    onClose={closeModal}
                    onUpdateExternalReferences={handleUpdateExternalReference}
                    onRemoveExternalReference={handleRemoveExternalReference}
                />
            )}

            {job?.manifest?.id && (
                <EmailJobModal
                    selectedJobIds={[job.id]}
                    open={modal === 'email-job'}
                    manifestId={job.manifest.id}
                    onClose={closeModal}
                    onSuccess={() => {
                        closeModal();
                        setHasEmailBeenSent(true);
                    }}
                />
            )}
        </StyledPageWrapper>
    );
};

export default ManifestJobDetails;
